import { ConnectError } from '@connectrpc/connect';
import { ZodError } from 'zod';

import { leaveBreadcrumb } from 'cfa-react-logging';
import ooeConstants from '../constants';
import { d2cRestaurantClient } from '../shared/clients';
import { ValidatedDeliveryAddress } from '../order/schemas';
import { AddressResult } from './schemas';
import {
  addressResultToAutoComplete,
  autoCompleteToAddressResult,
  physicalToValidatedDeliveryAddress,
} from './transforms';

export const getAutoCompleteResults = async (
  partialAddress: string,
  signal?: AbortSignal,
): Promise<AddressResult[]> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_LOCATIONS,
    context: 'Get AutoComplete Result (Protobuf)',
    breadcrumb: 'Get AutoComplete Result (Protobuf)',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    message: 'Calling protobuf GetAutoCompleteResult RPC',
    partialAddress,
  });

  try {
    const { autocompleteResults } = await d2cRestaurantClient.getAutoCompleteResult(
      { partialAddress },
      { signal },
    );

    return autocompleteResults.map((result) => autoCompleteToAddressResult.parse(result));
  } catch (error) {
    if (error instanceof ConnectError) {
      leaveBreadcrumb('Get AutoComplete Result (Protobuf) - Error', {
        message: 'ConnectRPC error during autocomplete',
        code: error.code,
        errorMessage: error.message,
      });
      throw new Error(error.message || 'Address autocomplete failed');
    }

    leaveBreadcrumb('Get AutoComplete Result (Protobuf) - Error', {
      message: 'Unexpected error during autocomplete',
      error: String(error),
    });
    throw error;
  }
};

export const validateAddressFromProtobuf = async (
  addressResult: AddressResult,
  signal?: AbortSignal,
): Promise<ValidatedDeliveryAddress> => {
  const bugsnag = {
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_LOCATIONS,
    context: 'Validate Address (Protobuf)',
    breadcrumb: 'Validate Address (Protobuf)',
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    message: 'Calling protobuf ValidateAddress RPC',
    placeId: addressResult.placeId,
    useProtobuf: true,
  });

  try {
    const autoComplete = addressResultToAutoComplete.parse(addressResult);
    const response = await d2cRestaurantClient.validateAddress(
      {
        autocompleteResultWithSessionToken: {
          autocompleteResult: autoComplete,
        },
      },
      { signal },
    );

    if (!response.physicalAddress) {
      throw new Error('No physical address returned from validation API');
    }

    const validatedAddress = physicalToValidatedDeliveryAddress.parse(response.physicalAddress);

    leaveBreadcrumb('Validate Address (Protobuf) - Success', {
      message: 'Successfully validated address via protobuf',
      hasStreetNumber: Boolean(validatedAddress.streetNumber),
      hasRoute: Boolean(validatedAddress.route),
    });

    return validatedAddress;
  } catch (error) {
    if (error instanceof ZodError) {
      leaveBreadcrumb('Validate Address (Protobuf) - Error', {
        message: 'Zod validation error during address validation',
        zodIssues: error.issues,
        errorMessage: error.message,
      });
      throw new Error('Invalid address data format');
    }

    if (error instanceof ConnectError) {
      leaveBreadcrumb('Validate Address (Protobuf) - Error', {
        message: 'ConnectRPC error during address validation',
        code: error.code,
        errorMessage: error.message,
      });
      throw new Error(error.message || 'Address validation failed');
    }

    leaveBreadcrumb('Validate Address (Protobuf) - Error', {
      message: 'Unexpected error during address validation',
      error: String(error),
    });
    throw error;
  }
};
